import { useEffect } from 'react';

const ALT_SCRIPT_SRC = 'https://d1bxh8uas1mnw7.cloudfront.net/assets/embed.js';
const PLUMX_SCRIPT_SRC = 'https://cdn.plu.mx/widget-popup.js';

function safeRemove(el) {
  try { el?.remove?.(); } catch (e) { /* ignore */ }
}

function qsa(sel, root = document) {
  try { return Array.from(root.querySelectorAll(sel)); } catch (e) { return []; }
}

function qs(sel, root = document) {
  try { return root.querySelector(sel); } catch (e) { return null; }
}

export function removeAltmetricArtifacts() {
  const existing = qs(`script[src*="d1bxh8uas1mnw7.cloudfront.net/assets/embed.js"]`);
  if (existing?.parentNode) safeRemove(existing);

  if (typeof window !== 'undefined' && window.altmetric) {
    try { delete window.altmetric; } catch (e) { /* ignore */ }
  }

  const embedScript = qs('#altmetric-embed-js');
  if (embedScript?.parentNode) safeRemove(embedScript);
}

export function removePlumxArtifacts({ removeRenderedDom = true, renderedSelector = '.plx-wrapping-print-link' } = {}) {
  const existing = qs(`script[src*="cdn.plu.mx/widget-popup.js"]`);
  if (existing?.parentNode) safeRemove(existing);

  if (removeRenderedDom && renderedSelector) {
    qsa(renderedSelector).forEach((el) => safeRemove(el));
  }
}

export function wireUpPlumx() {
  try {
    if (typeof window !== 'undefined'
      && window.__plumX
      && window.__plumX.widgets
      && window.__plumX.widgets.popup
      && typeof window.__plumX.widgets.popup.wireUp === 'function') {
      window.__plumX.widgets.popup.wireUp();
      return true;
    }
  } catch (e) { /* ignore */ }
  return false;
}

function appendScript({ src, type, async = true, onload } = {}) {
  const s = document.createElement('script');
  if (type) s.type = type;
  s.src = src;
  s.async = !!async;
  if (typeof onload === 'function') s.onload = onload;
  document.body.appendChild(s);
  return s;
}

/**
 * useMetricsWidgets
 * - Altmetric/PlumX 스크립트 삭제/로드/재스캔을 공통화
 *
 * @param {object} params
 * @param {boolean} params.enabled - 실행 여부 (예: !loading && rows.length>0)
 * @param {object} params.delaysMs - { altmetric: number, plumx: number } (각각 지연)
 * @param {boolean} params.cleanupBeforeLoad - 로드 전에 삭제/초기화 할지 여부
 * @param {boolean} params.plumxRemoveRenderedDom - PlumX가 변환한 DOM(.plx-wrapping-print-link)도 지울지
 * @param {string} params.plumxRenderedSelector - 지울 PlumX 렌더 DOM 셀렉터
 * @param {number} params.plumxWireUpDelayMs - PlumX 스크립트 onload 후 추가 wireUp 지연(기본 150ms)
 */
export function useMetricsWidgets({
  enabled,
  delaysMs = { altmetric: 1000, plumx: 1000 },
  cleanupBeforeLoad = true,
  plumxRemoveRenderedDom = true,
  plumxRenderedSelector = '.plx-wrapping-print-link',
  plumxWireUpDelayMs = 150,
} = {}) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!enabled) return;

    let altmetricTid = null;
    let plumxTid = null;
    let plumxWireTid = null;

    if (cleanupBeforeLoad) {
      removeAltmetricArtifacts();
      removePlumxArtifacts({ removeRenderedDom: plumxRemoveRenderedDom, renderedSelector: plumxRenderedSelector });
    }

    altmetricTid = setTimeout(() => {
      if (cleanupBeforeLoad) removeAltmetricArtifacts();
      appendScript({ src: ALT_SCRIPT_SRC, async: true });
    }, Number(delaysMs?.altmetric ?? 0));

    plumxTid = setTimeout(() => {
      if (cleanupBeforeLoad) removePlumxArtifacts({ removeRenderedDom: plumxRemoveRenderedDom, renderedSelector: plumxRenderedSelector });
      appendScript({
        src: PLUMX_SCRIPT_SRC,
        type: 'text/javascript',
        async: true,
        onload: () => {
          wireUpPlumx();
          if (plumxWireUpDelayMs != null) {
            plumxWireTid = setTimeout(() => wireUpPlumx(), Number(plumxWireUpDelayMs) || 0);
          }
        },
      });
    }, Number(delaysMs?.plumx ?? 0));

    return () => {
      if (altmetricTid) clearTimeout(altmetricTid);
      if (plumxTid) clearTimeout(plumxTid);
      if (plumxWireTid) clearTimeout(plumxWireTid);
    };
  }, [
    enabled,
    Number(delaysMs?.altmetric ?? 0),
    Number(delaysMs?.plumx ?? 0),
    cleanupBeforeLoad,
    plumxRemoveRenderedDom,
    plumxRenderedSelector,
    Number(plumxWireUpDelayMs ?? 0),
  ]);
}


